import { Box } from '@mui/material'
import { useState } from 'react'
import { useOrgChartUsersQuery } from 'shared/schema'
import theme from 'shared/theme'
import { useAuthGate } from 'src/components/AuthGate'
import Container from 'src/components/Container'
import OrgChartFollow, { Av, OrgNode } from 'src/components/OrgChartFollow'
import { OrgChartPDFButton } from './OrgChartPDFButton'

// Show the person's real photo, falling back to the chart's initials circle when
// they have none or the image fails to load. UserProfilePhoto isn't reused here
// because it falls back to a default silhouette rather than initials.
function OrgChartAvatar({
	person,
	isMe,
	size
}: {
	person: OrgNode
	isMe: boolean
	size: number
}) {
	const [failed, setFailed] = useState(false)
	const src = person.photoUrl as string | undefined

	if (!src || failed) {
		return <Av person={person} isMe={isMe} size={size} accentColor={theme.colors.primary} />
	}

	return (
		<img
			src={src}
			width={size}
			height={size}
			onError={() => setFailed(true)}
			style={{
				borderRadius: 0,
				objectFit: 'cover',
				flexShrink: 0,
				// Matches the accent ring Av draws for the signed-in user.
				boxShadow: isMe
					? `0 0 0 2px ${theme.colors.primary},0 0 12px ${theme.colors.primary}55`
					: 'none'
			}}
		/>
	)
}

function OrgChartRoute() {
	const { data, loading, error } = useOrgChartUsersQuery()
	const { user } = useAuthGate()

	if (loading) {
		return <Container>Loading...</Container>
	}

	if (error) {
		return <Container>Error loading org chart: {error.message}</Container>
	}

	if (data?.users) {
		const ids = new Set(data.users.map((u) => u.id))

		const nodes: OrgNode[] = data.users.map((u) => ({
			id: u.id,
			name: `${u.firstName} ${u.lastName}`,
			title: u.jobTitle,
			// Normalize supervisors that aren't in the fetched set to a root (null).
			managerId: u.supervisorId && ids.has(u.supervisorId) ? u.supervisorId : null,
			photoUrl: u.profilePhotoUrl || undefined,
			isYou: u.id === user?.id,
			// Carried through for PersonDetailModal (opened on card click).
			firstName: u.firstName,
			lastName: u.lastName,
			jobTitle: u.jobTitle,
			profilePhotoUrl: u.profilePhotoUrl,
			department: u.department,
			jobsite: u.jobsite,
			email: u.email,
			phoneCell: u.phoneCell,
			phoneDirect: u.phoneDirect,
			phoneExt: u.phoneExt
		}))

		// Drop root-level people who have no reports (board members, broken data) so
		// the chart anchors on the real operational top of org — matches the prior
		// route's `filteredOrgChartData` behavior.
		const hasReports = new Set(nodes.map((n) => n.managerId))
		const chartNodes = nodes.filter((n) => n.managerId != null || hasReports.has(n.id))

		// Anyone whose supervisor isn't in the fetched set becomes a root, so the data
		// is a forest, not a tree. OrgChartFollow renders a single lineage from one
		// root, so give each root its own chart — the prior route did the same by
		// mapping over `rootEmployees`. Passing the whole flat list to one chart would
		// silently hide every root but the first, along with everyone beneath it.
		const childrenOf = new Map<number, OrgNode[]>()
		chartNodes.forEach((n) => {
			if (n.managerId != null) {
				childrenOf.set(n.managerId, [...(childrenOf.get(n.managerId) ?? []), n])
			}
		})
		const subtreeOf = (root: OrgNode) => {
			const out: OrgNode[] = []
			const seen = new Set<number>()
			const walk = (n: OrgNode) => {
				// supervisor_id has no DB cycle constraint and is written by the ADP
				// sync, so guard the descent rather than recurse forever.
				if (seen.has(n.id)) return
				seen.add(n.id)
				out.push(n)
				;(childrenOf.get(n.id) ?? []).forEach(walk)
			}
			walk(root)
			return out
		}
		const roots = chartNodes.filter((n) => n.managerId == null)

		return (
			<Container>
				<Box sx={{ textAlign: 'right' }}>
					<OrgChartPDFButton />
				</Box>
				{roots.map((root) => (
					<OrgChartFollow
						key={root.id}
						nodes={subtreeOf(root)}
						rootId={root.id}
						accentColor={theme.colors.primary}
						renderAvatar={OrgChartAvatar}
					/>
				))}
			</Container>
		)
	}
	return null
}

export default OrgChartRoute
